/**
 * Phase 24.7.4 T2 — shared recipe content component.
 * Phase 24.7.4 T1 — per-step cooking timers.
 *
 * Renders ingredients + steps + macros for both modal mode (RecipeDetailModal)
 * and standalone page mode (/recipes/:id). Excludes modal-specific actions
 * (mark-cooked, swap, feedback) which only apply in meal-plan context.
 *
 * Per-step timers (T1):
 * - Steps with `timer_seconds > 0` get Start/Pause/Reset countdown UI.
 * - Multiple concurrent timers — independent state per step.position.
 * - Browser Notification API + optional audio beep on completion.
 * - Permission request inline (user-gesture; Safari enforces).
 */
import { useEffect, useRef, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Play, Pause, RotateCcw, Bell, BellOff, BellRing } from "lucide-react";
import { Button } from "@/components/ui/Button";
import type { RecipeFullRead } from "@/api/recipes";

interface Props {
  recipe: RecipeFullRead;
  showDescription?: boolean;
  /**
   * Phase 24.7.4 T4 — multiply ingredient amounts + per-serving macros by
   * this factor. Default 1.0 = render recipe as-is. RecipeDetailModal
   * passes the meal-plan item's `servings` so live UI matches selector.
   */
  servingsMultiplier?: number;
}

type TimerStatus = "idle" | "running" | "paused" | "completed";

interface TimerState {
  remainingMs: number;
  status: TimerStatus;
  startedAt: number | null; // wall-clock ms when running
  pausedAtRemainingMs: number; // remaining when paused (or original total when starting fresh)
}

function formatTime(ms: number): string {
  const totalSec = Math.max(0, Math.ceil(ms / 1000));
  const min = Math.floor(totalSec / 60);
  const sec = totalSec % 60;
  return `${min}:${String(sec).padStart(2, "0")}`;
}

export function RecipeDetailContent({
  recipe,
  showDescription = true,
  servingsMultiplier = 1.0,
}: Props) {
  const { t } = useTranslation();
  const mult = servingsMultiplier > 0 ? servingsMultiplier : 1.0;
  const [timers, setTimers] = useState<Map<number, TimerState>>(new Map());
  const intervalRef = useRef<number | null>(null);

  const fireCompletionNotification = useCallback(
    (stepPosition: number) => {
      if (typeof Notification !== "undefined" && Notification.permission === "granted") {
        try {
          new Notification(t("recipe_timer.completed_title"), {
            body: t("recipe_timer.completed_body", { step: stepPosition }),
            icon: "/icons/icon-192.png",
          });
        } catch {
          // Notification constructor may throw in some browsers if invoked from
          // unexpected context — non-fatal, audio/visual cues still surface.
        }
      }
    },
    [t],
  );

  // Tick loop — single interval drives all running timers.
  useEffect(() => {
    const hasRunning = Array.from(timers.values()).some((s) => s.status === "running");
    if (hasRunning && intervalRef.current === null) {
      intervalRef.current = window.setInterval(() => {
        setTimers((prev) => {
          const next = new Map(prev);
          let mutated = false;
          for (const [pos, timer] of next.entries()) {
            if (timer.status !== "running" || timer.startedAt == null) continue;
            const elapsedMs = Date.now() - timer.startedAt;
            const remainingMs = Math.max(0, timer.pausedAtRemainingMs - elapsedMs);
            if (remainingMs <= 0) {
              next.set(pos, {
                ...timer,
                status: "completed",
                remainingMs: 0,
                startedAt: null,
              });
              mutated = true;
              fireCompletionNotification(pos);
            } else if (Math.abs(remainingMs - timer.remainingMs) >= 500) {
              // Update display ~twice per second без excess rerenders
              next.set(pos, { ...timer, remainingMs });
              mutated = true;
            }
          }
          return mutated ? next : prev;
        });
      }, 250);
    }
    if (!hasRunning && intervalRef.current !== null) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    return () => {
      if (intervalRef.current !== null) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [timers, fireCompletionNotification]);

  function startTimer(stepPosition: number, totalSeconds: number) {
    setTimers((prev) => {
      const next = new Map(prev);
      const existing = next.get(stepPosition);
      if (existing && existing.status === "paused") {
        // Resume from paused state — keep pausedAtRemainingMs as anchor
        next.set(stepPosition, {
          ...existing,
          status: "running",
          startedAt: Date.now(),
        });
      } else {
        // Fresh start (idle, completed, or new)
        const totalMs = totalSeconds * 1000;
        next.set(stepPosition, {
          remainingMs: totalMs,
          status: "running",
          startedAt: Date.now(),
          pausedAtRemainingMs: totalMs,
        });
      }
      return next;
    });
  }

  function pauseTimer(stepPosition: number) {
    setTimers((prev) => {
      const next = new Map(prev);
      const existing = next.get(stepPosition);
      if (!existing || existing.status !== "running" || existing.startedAt == null) {
        return prev;
      }
      const elapsedMs = Date.now() - existing.startedAt;
      const newRemaining = Math.max(0, existing.pausedAtRemainingMs - elapsedMs);
      next.set(stepPosition, {
        ...existing,
        status: "paused",
        remainingMs: newRemaining,
        pausedAtRemainingMs: newRemaining,
        startedAt: null,
      });
      return next;
    });
  }

  function resetTimer(stepPosition: number) {
    setTimers((prev) => {
      if (!prev.has(stepPosition)) return prev;
      const next = new Map(prev);
      next.delete(stepPosition);
      return next;
    });
  }

  return (
    <div className="space-y-4" data-testid="recipe-detail-content">
      {recipe.image_url ? (
        <img
          src={recipe.image_url}
          alt=""
          className="aspect-square w-full max-h-96 rounded-md object-cover"
          loading="lazy"
          data-testid="recipe-detail-image"
        />
      ) : (
        <div
          className="flex h-32 w-full items-center justify-center rounded-md border border-cream-border bg-cream text-[12px] text-taupe"
          data-testid="recipe-detail-image-placeholder"
        >
          {t("meal_plan.no_photo")}
        </div>
      )}
      {showDescription && recipe.description ? (
        <p className="text-[13px] text-taupe">{recipe.description}</p>
      ) : null}

      {/* Macros (scaled by servingsMultiplier — Phase 24.7.4 T4) */}
      <div className="rounded-md bg-cream p-3 text-[12px] text-taupe">
        <span className="metric-number text-navy-deep">
          {Math.round(recipe.kcal_per_serving * mult)}
        </span>{" "}
        {t("nutrition_page.kcal")} · {t("nutrition_page.p")}{" "}
        <span className="metric-number">{Math.round(recipe.protein_g * mult)}</span>{" "}
        · {t("nutrition_page.c")}{" "}
        <span className="metric-number">{Math.round(recipe.carbs_g * mult)}</span>{" "}
        · {t("nutrition_page.f")}{" "}
        <span className="metric-number">{Math.round(recipe.fat_g * mult)}</span>
        {" · "}
        {t("meal_plan.prep_time", { n: recipe.prep_time_min })}
      </div>

      {/* Ingredients */}
      {recipe.ingredients.length > 0 ? (
        <div>
          <h3 className="text-[11px] font-medium uppercase tracking-wider text-amber-dark">
            {t("meal_plan.ingredients")}
          </h3>
          <ul className="mt-2 space-y-1 text-[13px]">
            {recipe.ingredients.map((ing, i) => (
              <li key={i} className="flex justify-between gap-2">
                <span className="text-navy-deep">
                  {ing.name}
                  {ing.is_optional ? (
                    <span className="ml-1 text-[10px] text-taupe">
                      ({t("meal_plan.optional")})
                    </span>
                  ) : null}
                </span>
                <span className="metric-number text-taupe">
                  {Number((ing.amount * mult).toFixed(1))} {ing.unit}
                </span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {/* Steps */}
      {recipe.steps.length > 0 ? (
        <div>
          <div className="flex items-center justify-between">
            <h3 className="text-[11px] font-medium uppercase tracking-wider text-amber-dark">
              {t("meal_plan.steps")}
            </h3>
            <NotificationPermissionButton />
          </div>
          <ol className="mt-2 space-y-3 text-[13px]">
            {recipe.steps.map((step) => {
              const timer = timers.get(step.position);
              const hasTimer = !!step.timer_seconds && step.timer_seconds > 0;
              const displayMs =
                timer?.remainingMs ?? (step.timer_seconds ?? 0) * 1000;

              return (
                <li
                  key={step.position}
                  className="space-y-1.5"
                  data-testid={`step-${step.position}`}
                >
                  <div className="flex gap-2">
                    <span className="metric-number shrink-0 text-amber-dark">
                      {step.position}.
                    </span>
                    <span className="text-navy-deep">{step.content}</span>
                  </div>
                  {hasTimer ? (
                    <div
                      className="ml-5 flex flex-wrap items-center gap-2"
                      data-testid={`timer-row-${step.position}`}
                    >
                      <span
                        className="metric-number text-[13px] tabular-nums text-navy-deep"
                        data-testid={`timer-display-${step.position}`}
                      >
                        {formatTime(displayMs)}
                      </span>
                      {timer?.status === "completed" ? (
                        <>
                          <span
                            className="text-[11px] font-medium text-amber-dark"
                            data-testid={`timer-completed-${step.position}`}
                          >
                            {t("recipe_timer.completed_label")}
                          </span>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => resetTimer(step.position)}
                            data-testid={`timer-reset-${step.position}`}
                          >
                            <RotateCcw className="h-3 w-3" strokeWidth={2} />
                            {t("recipe_timer.reset")}
                          </Button>
                        </>
                      ) : timer?.status === "running" ? (
                        <>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => pauseTimer(step.position)}
                            data-testid={`timer-pause-${step.position}`}
                          >
                            <Pause className="h-3 w-3" strokeWidth={2} />
                            {t("recipe_timer.pause")}
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => resetTimer(step.position)}
                            data-testid={`timer-reset-${step.position}`}
                          >
                            <RotateCcw className="h-3 w-3" strokeWidth={2} />
                            {t("recipe_timer.reset")}
                          </Button>
                        </>
                      ) : (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() =>
                            startTimer(step.position, step.timer_seconds!)
                          }
                          data-testid={`timer-start-${step.position}`}
                        >
                          <Play className="h-3 w-3" strokeWidth={2} />
                          {t("recipe_timer.start")}
                        </Button>
                      )}
                    </div>
                  ) : null}
                </li>
              );
            })}
          </ol>
        </div>
      ) : null}
    </div>
  );
}

/**
 * Permission request UI for browser notifications.
 * Renders nothing if Notification API unsupported (e.g. SSR test env пока mocks set).
 */
function NotificationPermissionButton() {
  const { t } = useTranslation();
  const [permission, setPermission] = useState<NotificationPermission | "unsupported">(
    () => (typeof Notification !== "undefined" ? Notification.permission : "unsupported"),
  );

  if (permission === "unsupported") return null;

  if (permission === "granted") {
    return (
      <span
        className="inline-flex items-center gap-1 text-[10px] text-amber-dark"
        data-testid="notif-permission-granted"
      >
        <BellRing className="h-3 w-3" strokeWidth={2} />
        {t("recipe_timer.notifications_enabled")}
      </span>
    );
  }

  if (permission === "denied") {
    return (
      <span
        className="inline-flex items-center gap-1 text-[10px] text-taupe"
        data-testid="notif-permission-denied"
      >
        <BellOff className="h-3 w-3" strokeWidth={1.8} />
        {t("recipe_timer.notifications_blocked")}
      </span>
    );
  }

  return (
    <button
      type="button"
      onClick={async () => {
        try {
          const result = await Notification.requestPermission();
          setPermission(result);
        } catch {
          setPermission("denied");
        }
      }}
      className="inline-flex items-center gap-1 text-[11px] font-medium text-amber-dark underline-offset-2 hover:underline"
      data-testid="notif-permission-request"
    >
      <Bell className="h-3 w-3" strokeWidth={2} />
      {t("recipe_timer.enable_notifications")}
    </button>
  );
}
